import { useCallback } from 'react';
import { get, post } from '@/utils/apiClient';
import { toast } from 'react-toastify';
import { logger } from '@/utils/logger';

export interface WorkflowAdvanceResponse {
  message?: string;
  is_complete?: boolean;
  advanced?: boolean;
  next_encounter?: {
    patient_id?: string;
    patient_name?: string;
    encounter_id?: string;
  } | null;
  progress?: { completed: number; total: number } | null;
  current_patient?: {
    patient_id?: string;
    patient_name?: string;
  } | null;
}

export interface UseGuidedWorkflowDispositionParams {
  guidedMode: string | null;
  currentSessionId: string | null;
  clearNoteWorkflowState: () => void;
  setCurrentPatient: (patient: { id: string; name: string } | null) => void;
  setCurrentEncounter: (encounter: { id: string } | null) => void;
  setAiCurrentPatient: (patient: { patient_id: string; patient_name: string } | null) => void;
  appendAssistantMessage: (text: string) => void;
  fetchGuidedWorkflowData: (mode: string) => Promise<void>;
}

const GUIDED_WORKFLOW_ADVANCE_RETRIES = 3;
const GUIDED_WORKFLOW_ADVANCE_RETRY_DELAY_MS = 1000;

export function useGuidedWorkflowDisposition({
  guidedMode,
  currentSessionId,
  clearNoteWorkflowState,
  setCurrentPatient,
  setCurrentEncounter,
  setAiCurrentPatient,
  appendAssistantMessage,
  fetchGuidedWorkflowData,
}: UseGuidedWorkflowDispositionParams) {
  const applyNextEncounterContext = useCallback(
    (data: WorkflowAdvanceResponse) => {
      if (data?.next_encounter?.patient_id && data?.next_encounter?.patient_name) {
        setCurrentPatient({
          id: data.next_encounter.patient_id,
          name: data.next_encounter.patient_name,
        });
        setCurrentEncounter({ id: data.next_encounter.encounter_id });
        setAiCurrentPatient({
          patient_id: data.next_encounter.patient_id,
          patient_name: data.next_encounter.patient_name,
        });
      } else if (data?.current_patient?.patient_id && data?.current_patient?.patient_name) {
        setCurrentPatient({
          id: data.current_patient.patient_id,
          name: data.current_patient.patient_name,
        });
        setCurrentEncounter(null);
        setAiCurrentPatient({
          patient_id: data.current_patient.patient_id,
          patient_name: data.current_patient.patient_name,
        });
      } else {
        setCurrentPatient(null);
        setCurrentEncounter(null);
        setAiCurrentPatient(null);
      }
    },
    [setCurrentPatient, setCurrentEncounter, setAiCurrentPatient]
  );

  const handleDisposition = useCallback(
    async (disposition: 'sign' | 'save' | 'discard', encounterId?: string, noteId?: string) => {
      const isGuided = guidedMode === 'guided_today' || guidedMode === 'guided_pending';

      if (!isGuided) {
        clearNoteWorkflowState();
        return;
      }

      if (!currentSessionId || !encounterId) {
        clearNoteWorkflowState();
        if (guidedMode) {
          fetchGuidedWorkflowData(guidedMode).catch((err: unknown) => {
            logger.error('Failed to refresh guided workflow data:', err);
          });
        }
        return;
      }

      clearNoteWorkflowState();

      if (disposition === 'discard') {
        if (guidedMode) {
          fetchGuidedWorkflowData(guidedMode).catch((err: unknown) => {
            logger.error('Failed to refresh guided workflow data after discard:', err);
          });
        }
        return;
      }

      const advance = async (attempt: number): Promise<WorkflowAdvanceResponse | null> => {
        try {
          const response = await post(
            '/api/guided-notes/workflow/advance',
            {
              session_id: currentSessionId,
              disposition,
              encounter_id: encounterId,
            },
            { suppressServerErrorToast: true }
          );
          return (await response.json()) as WorkflowAdvanceResponse;
        } catch (err) {
          logger.error(`Guided workflow advance attempt ${attempt} failed:`, err);
          if (attempt < GUIDED_WORKFLOW_ADVANCE_RETRIES) {
            await new Promise((resolve) =>
              setTimeout(resolve, GUIDED_WORKFLOW_ADVANCE_RETRY_DELAY_MS * attempt)
            );
            return advance(attempt + 1);
          }
          return null;
        }
      };

      let data = await advance(1);

      if (!data) {
        toast.error("We couldn't move to the next encounter. Checking the latest workflow status…");
        try {
          const reconcileResponse = await get('/api/guided-notes/workflow/active', {
            suppressServerErrorToast: true,
          });
          if (reconcileResponse.ok) {
            const active = (await reconcileResponse.json()) as {
              workflow?: {
                current_index?: number;
                total_count?: number;
                completed_count?: number;
                encounters?: Array<{
                  encounter_id?: string;
                  patient_id?: string;
                  patient_name?: string;
                }>;
              } | null;
            } | null;
            const workflow = active?.workflow;
            const encounters = workflow?.encounters || [];
            const currentIndex = workflow?.current_index ?? 0;
            const totalCount = workflow?.total_count ?? encounters.length;
            const completedCount = workflow?.completed_count ?? currentIndex;

            if (workflow && currentIndex < totalCount && currentIndex < encounters.length) {
              const next = encounters[currentIndex];
              setCurrentPatient({ id: next.patient_id || '', name: next.patient_name || '' });
              setCurrentEncounter({ id: next.encounter_id || '' });
              setAiCurrentPatient({
                patient_id: next.patient_id || '',
                patient_name: next.patient_name || '',
              });
              data = {
                advanced: false,
                is_complete: false,
                message: undefined,
                next_encounter: next,
                current_patient: null,
                progress: { completed: completedCount, total: totalCount },
              };
            } else if (workflow) {
              setCurrentPatient(null);
              setCurrentEncounter(null);
              setAiCurrentPatient(null);
              data = {
                advanced: false,
                is_complete: true,
                message: undefined,
                next_encounter: null,
                current_patient: null,
                progress: { completed: completedCount, total: totalCount },
              };
            }
          }
        } catch (reconcileErr) {
          logger.error('Failed to reconcile guided workflow state:', reconcileErr);
        }
      }

      if (data) {
        if (data.message) {
          appendAssistantMessage(data.message);
        }
        applyNextEncounterContext(data);
      } else {
        appendAssistantMessage(
          "I wasn't able to move to the next encounter. Please try again or refresh the page."
        );
      }

      if (guidedMode) {
        fetchGuidedWorkflowData(guidedMode).catch((err: unknown) => {
          logger.error('Failed to refresh guided workflow data:', err);
        });
      }
    },
    [
      guidedMode,
      currentSessionId,
      clearNoteWorkflowState,
      setCurrentPatient,
      setCurrentEncounter,
      setAiCurrentPatient,
      appendAssistantMessage,
      fetchGuidedWorkflowData,
      applyNextEncounterContext,
    ]
  );

  return handleDisposition;
}
